// useAuthStore — current session state for the SPA.
//
// Hydrated once on boot from ``GET /api/auth/session``. The login form
// calls ``signIn`` after a successful ``POST /api/auth/login``; the
// logout button calls ``signOut`` after ``POST /api/auth/logout``.
//
// ``ready`` flips to true after the first hydration round-trip so
// router-level guards can wait on it before deciding whether to
// redirect to /login. ``authEnabled = false`` means the backend has no
// credentials configured (the dev/test default) and the SPA should
// render the app directly without ever showing the login page.

import { create } from 'zustand'

import { api, type SessionInfo } from '@/lib/api'

interface AuthStore {
  user: string | null
  authEnabled: boolean
  ready: boolean
  /** Pull the current session from the backend and update local state. */
  hydrate: () => Promise<void>
  /** Called by the login page after a successful POST /api/auth/login. */
  signIn: (session: SessionInfo) => void
  /** Called by the logout button (also runs the network request). */
  signOut: () => Promise<void>
}

export const useAuthStore = create<AuthStore>((set) => ({
  user: null,
  authEnabled: false,
  ready: false,

  hydrate: async () => {
    try {
      const session = await api.auth.session()
      set({
        user: session.user,
        authEnabled: session.auth_enabled,
        ready: true,
      })
    } catch {
      // The /auth/session endpoint is in the api.ts redirect skip-list,
      // so a 401 here doesn't navigate — it just means "no session".
      // Flip ready so the router stops gating on hydration.
      set({ user: null, authEnabled: true, ready: true })
    }
  },

  signIn: (session) =>
    set({
      user: session.user,
      authEnabled: session.auth_enabled,
      ready: true,
    }),

  signOut: async () => {
    try {
      await api.auth.logout()
    } catch {
      // Even if the network call fails we still want the UI to forget
      // the user — the cookie is gone for all practical purposes.
    }
    set({ user: null })
  },
}))
